import { Icon16Add, Icon20AddAlt } from '@vkontakte/icons';
import { Text } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { type FC, useCallback, useMemo } from 'react';
import { IS_BIZ_USER } from 'reactApp/appHelpers/configHelpers';
import { animateNewTableGrid } from 'reactApp/appHelpers/featuresHelpers/features/datalistNewTableGrid';
import { showUploadItemsOnDatalistContext } from 'reactApp/appHelpers/featuresHelpers/features/uploadItemsOnDatalistContext';
import { EStorageType } from 'reactApp/modules/storage/storage.types';

import styles from './DataListItemCreateNew.css';
import { type CreateNewMod, CREATE_ITEM_DATA } from './DataListItemCreateNew.data';

const CREATE_CAPTION = IS_BIZ_USER ? 'Создать' : 'Создать документ\nили папку';
const NEW_ITEM_CAPTION = showUploadItemsOnDatalistContext ? 'Создать\nили загрузить' : CREATE_CAPTION;

interface DataListItemCreateNewProps {
    className?: string;
    isThumb: boolean;
    isSharedFolderOnly?: boolean;
    onClick?: (e: React.MouseEvent) => void;
    onContextMenu?: (e: React.MouseEvent) => void;
    mod?: CreateNewMod;
    rootRef?: React.Ref<HTMLDivElement>;
    storage?: EStorageType;
}

export const DataListItemCreateNew: FC<DataListItemCreateNewProps> = ({
    className,
    onClick,
    onContextMenu,
    mod,
    isThumb,
    isSharedFolderOnly,
    storage,
}) => {
    const handleOnClick = useCallback(
        (e) => {
            const itemData = mod && CREATE_ITEM_DATA[mod];

            if (itemData?.onClick) {
                return itemData.onClick(e);
            }

            onClick?.(e);
        },
        [mod, onClick]
    );

    const handleOnContextMenu = useCallback(
        (e) => {
            const itemData = mod && CREATE_ITEM_DATA[mod];

            if (itemData?.onContextMenu) {
                itemData.onContextMenu(e);
                return;
            }

            onContextMenu?.(e);
        },
        [mod, onContextMenu]
    );

    const caption = useMemo(() => {
        const itemData = mod && CREATE_ITEM_DATA[mod];

        if (itemData?.text) {
            return itemData?.text;
        }

        if (isSharedFolderOnly) {
            return 'Создать общую папку';
        }

        if (storage === EStorageType.sharedAutodelete) {
            return IS_BIZ_USER ? 'Создать' : 'Создать папку';
        }

        return isThumb ? NEW_ITEM_CAPTION : NEW_ITEM_CAPTION.replace('\n', ' ');
    }, [isSharedFolderOnly, isThumb, mod, storage]);

    const itemData = mod && CREATE_ITEM_DATA[mod];
    const icon = itemData?.icon || isThumb ? <Icon20AddAlt /> : <Icon16Add />;

    return (
        <button
            className={classNames(styles.root, className, {
                [styles.root_thumb]: isThumb,
                [styles[`root_${mod}`]]: Boolean(mod),
                [styles.root_animate]: isThumb && animateNewTableGrid,
            })}
            onClick={handleOnClick}
            onContextMenu={handleOnContextMenu}
        >
            <div className={styles.icon}>{icon}</div>
            <Text className={styles.text}>{caption}</Text>
        </button>
    );
};
